// contexts/TranslationContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

// Importar las traducciones (similar a tus archivos JSON)
import enTranslations from '../langs/en.json';
import esTranslations from '../langs/es.json';
import ptTranslations from '../langs/pt.json';
import frTranslations from '../langs/fr.json';
import deTranslations from '../langs/de.json';

const TranslationContext = createContext();

// Hook personalizado para usar las traducciones
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Función para aplanar objetos anidados (similar a tu flattenObject)
const flattenObject = (obj, prefix = '') => {
  const flattened = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        Object.assign(flattened, flattenObject(obj[key], newKey));
      } else {
        flattened[newKey] = obj[key];
      }
    }
  }
  
  return flattened;
};

// Provider del contexto
export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  
  const availableLanguages = ['es', 'en', 'pt', 'fr', 'de'];
  
  // Cargar idioma almacenado
  useEffect(() => {
    const storedLang = localStorage.getItem('selectedLanguage');
    if (storedLang && availableLanguages.includes(storedLang)) {
      setCurrentLanguage(storedLang);
    }
  }, []);
  
  // Cargar traducciones
  useEffect(() => {
    const loadTranslations = () => {
      const translationData = {
        en: enTranslations,
        es: esTranslations,
        pt: ptTranslations,
        fr: frTranslations,
        de: deTranslations
      };
      
      const processedTranslations = {};
      
      // Aplanar cada archivo de traducción
      Object.keys(translationData).forEach(lang => {
        const flattenedTranslations = flattenObject(translationData[lang]);
        
        Object.keys(flattenedTranslations).forEach(key => {
          if (!processedTranslations[key]) {
            processedTranslations[key] = {};
          }
          processedTranslations[key][lang] = flattenedTranslations[key];
        });
      });
      
      setTranslations(processedTranslations);
    };
    
    loadTranslations();
  }, []);
  
  // Función para cambiar idioma
  const setLanguage = (language) => {
    console.log(`Setting language to: ${language}`);
    if (availableLanguages.includes(language)) {
      setCurrentLanguage(language);
      localStorage.setItem('selectedLanguage', language);
    }
  };
  
  // Función para traducir (equivalente a tu translate del pipe)
  const translate = (key, params = {}) => {
    const translation = translations[key];
    
    if (!translation) {
      console.warn(`Translation key '${key}' not found`);
      return key;
    }
    
    let text = translation[currentLanguage] || translation['en'] || key;
    
    // Reemplazar parámetros si se proporcionan
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(`{{${param}}}`, params[param]);
      });
    }
    
    return text;
  };
  
  // Función para obtener etiqueta del idioma
  const getLanguageLabel = (lang) => {
    const labels = {
      'es': 'Español',
      'en': 'English',
      'pt': 'Português',
      'fr': 'Français',
      'de': 'Deutsch'
    };
    return labels[lang] || lang;
  };
  
  const value = {
    currentLanguage,
    availableLanguages,
    setLanguage,
    translate,
    getLanguageLabel
  };
  
  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Hook personalizado con funcionalidad adicional
export const useTranslate = () => {
  const { translate } = useTranslation();
  
  // Función equivalente a tu pipe
  const t = (key, params) => {
    return translate(key, params);
  };
  
  return { t, translate };
};